import { useState } from 'react';
import { Alert, Button, Chip, CircularProgress, Snackbar, Stack } from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  loanReviewShiftApi,
  LOAN_REVIEW_SHIFT_QUERY_KEY,
} from '../../api/adminConsole';
import { MANUAL_REVIEW_QUEUE_QUERY_KEY } from '../../api/admin';
import { formatShiftSince } from '../../lib/manualReviewAssignment';
import { messageFromOrchestratorAxiosError } from '../../lib/axiosErrorMessage';
import { isOrchestratorDisabled } from '../../lib/adminSession';

/**
 * Header control for loan-reviewer shifts.
 *
 * Hidden unless `GET /loan-review/shift` says the caller is in the Loan Reviewers group.
 * Super admins are excluded from that group by roster rules, so they never see this — which is
 * the point: they are not in the rotation.
 */
export function LoanReviewShiftToggle() {
  const qc = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);

  const shiftQ = useQuery({
    queryKey: LOAN_REVIEW_SHIFT_QUERY_KEY,
    queryFn: () => loanReviewShiftApi.current(),
    enabled: typeof window !== 'undefined' && !isOrchestratorDisabled(),
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });

  const invalidate = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: LOAN_REVIEW_SHIFT_QUERY_KEY }),
      qc.invalidateQueries({ queryKey: MANUAL_REVIEW_QUEUE_QUERY_KEY }),
    ]);
  };

  const start = useMutation({
    mutationFn: () => loanReviewShiftApi.start(),
    onSuccess: async () => {
      await invalidate();
    },
  });

  const end = useMutation({
    mutationFn: () => loanReviewShiftApi.end(),
    onSuccess: async (res) => {
      await invalidate();
      if (res.releasedApplicationId) {
        setNotice(
          `Your file went back to the front of the review queue (${res.releasedApplicationId}).`,
        );
      }
    },
  });

  if (!shiftQ.data?.isLoanReviewer) return null;

  const onShift = shiftQ.data.onShift;
  const since = formatShiftSince(shiftQ.data.shiftStartedAt);
  const busy = start.isPending || end.isPending;
  const error =
    (start.isError ? messageFromOrchestratorAxiosError(start.error) : null) ||
    (end.isError ? messageFromOrchestratorAxiosError(end.error) : null);

  return (
    <>
      {onShift ? (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
          <Chip
            size="small"
            color="success"
            variant="outlined"
            icon={<AccessTimeRoundedIcon sx={{ fontSize: '0.95rem' }} />}
            label={since ? `On shift since ${since}` : 'On shift'}
            sx={{
              height: 24,
              fontSize: '0.68rem',
              fontWeight: 700,
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          />
          <Button
            size="small"
            color="inherit"
            startIcon={busy ? <CircularProgress size={12} /> : <StopRoundedIcon />}
            disabled={busy}
            onClick={() => end.mutate()}
            sx={{
              height: 28,
              fontSize: '0.72rem',
              fontWeight: 700,
              textTransform: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            End shift
          </Button>
        </Stack>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={busy ? <CircularProgress size={12} /> : <PlayArrowRoundedIcon />}
          disabled={busy}
          onClick={() => start.mutate()}
          sx={{
            height: 28,
            fontSize: '0.72rem',
            fontWeight: 700,
            textTransform: 'none',
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          Start shift
        </Button>
      )}
      <Snackbar
        open={Boolean(notice || error)}
        autoHideDuration={8_000}
        onClose={() => {
          setNotice(null);
          start.reset();
          end.reset();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={error ? 'error' : 'info'}
          variant="filled"
          onClose={() => {
            setNotice(null);
            start.reset();
            end.reset();
          }}
        >
          {error || notice}
        </Alert>
      </Snackbar>
    </>
  );
}
